<template>
    <div class="plan-chooser flow__panel flow__panel--positioned-buttons">
        <error-handler class="plan-chooser__errors" />
        <hh-msg-display v-if="message"
                        class="plan-chooser__message"
                        type="warning"
                        :message="message" />
        <section class="plan-chooser__questions">
            <p class="plan-chooser__questions__info">
                Please give us some info to help us add some initial measures to your plan&hellip;
            </p>
            <motivation-chooser v-if="!scheme.hasFixedMotivation"
                                v-model="motivation"
                                class="plan-chooser__questions__motivation"
                                @input="onMotivationChange" />
            <budget-chooser v-if="showBudgetChooser"
                            v-model="budget"
                            class="plan-chooser__questions__budget" />
        </section>
        <branded-cta-tile name="build"
                          class="plan-chooser__tile"
                          button-text="Go to plan builder"
                          :show-back="true"
                          :disabled="!canProceed"
                          show-icon
                          @click="onConfirmClick"
                          @back-click="onBackClick" />
    </div>
</template>

<script>

import MOTIVATIONS from '@/models/MOTIVATIONS'

import PlanBuilderManager from '@/plan-builder-manager'
import ProgressManager from '@/progress-manager'

import AnalyticsPage from './analytics-page'
import MyHomeDataErrorsDisplay from './my-home-data-errors-display'
import PlanLoader from './plan-loader'

import BrandedCtaTile from '@/components/app-core/branded-cta-tile'
import BudgetChooser from '@/components/plan-chooser/budget-chooser'
import ErrorHandler from '@/components/app-core/error-handler'
import ResourcesHelper from '@/components/resources-helper'
import MotivationChooser from '@/components/plan-chooser/motivation-chooser'

export default {

    mixins: [
        AnalyticsPage,
        MyHomeDataErrorsDisplay,
        PlanLoader,
        ResourcesHelper,
    ],

    data() {
        return {
            name: 'Plan Chooser',

            /**
             * @returns {number}
             */
            budget: 0,

            /**
             * @returns {string}
             */
            motivation: '',
        }
    },

    computed: {
        /**
         * @returns {boolean}
         */
        showBudgetChooser() {
            if (this.scheme.hasFixedBudget) return false
            else if (this.motivation) return true

            return false
        },

        /**
         * Checks if the current state allows for proceeding.
         * @returns {boolean} - True if both budget and motivation are truthy, otherwise false.
         */
        canProceed() {
            if (this.scheme.hasFixedMotivation) return this.budget > 0
            if (this.scheme.hasFixedBudget) return !!this.motivation

            return this.budget > 0 && !!this.motivation && !this.loading
        },
    },

    mounted() {
        this.motivation = this.currentProject && this.currentProject.motivation ? this.currentProject.motivation : ''
        this.budget = this.currentProject && this.currentProject.budget ? this.currentProject.budget : 0
    },

    methods: {

        /**
         * Track an event related to plan building.
         * @param {string} type - The type of the plan event.
         * @param {string|number} value - The additional value associated with the event.
         */
        _track(type, value) {
            const action = `plan_${ type }_set`
            this.trackEvent('plan_build', action, {
                additional: value,
                label: type,
                value: value,
            })
            this.trackEvent('plan_build', action)
        },

        /**
         * Navigates to the MyHome route with the specified query.
         */
        onBackClick() {
            this.$router.push(ProgressManager.getPrevious(this.$route.name))
        },

        /**
         * Updates the motivation and budget properties and navigates the user to the Plan Builder view.
         */
        onConfirmClick() {
            if (!this.scheme.hasFixedMotivation) {
                PlanBuilderManager.setMotivationAsync(this.motivation)
                this._track('motivation', this.motivation)
            }

            if (!this.scheme.hasFixedBudget) {
                PlanBuilderManager.modifyBudgetAsync(this.budget)
                this._track('budget', this.budget)
            }

            this.$router.push(ProgressManager.getNext(this.$route.name))
        },

        /**
         * Updates the budget if using a targeting motivation where budget should not be considered.
         * @param {string} value - The new value of motivation.
         */
        onMotivationChange(value) {
            this.$logger.logObject({ value, motivation: this.motivation }, 'Motivation has changed', 'Plan Chooser')

            this.budget = value === MOTIVATIONS.targetNetZero || value === MOTIVATIONS.targetSapC
                ? Number.MAX_VALUE
                : 0
        },
    },

    components: {
        BrandedCtaTile,
        BudgetChooser,
        ErrorHandler,
        MotivationChooser,
    },
}

</script>

<style lang="scss">

// Ensure colours and other visual style elements are added to theme files, not placed in here.
// src\scss\schemes\views\_plan-chooser.scss

.plan-chooser {
    @include element('questions') {
        text-align: center;
        margin: 0;

        @include bp-min-small {
            > * {
                margin: 1rem auto 0 auto;
                display: inline-block;
            }
        }

        @include element('info') {
            margin-top: 0;
            display: block;

            @include bp-min-small {
                text-align: center;
            }
        }
    }

    @include element('tile') {
        @include wizard-page-tile;
    }
}

</style>
